import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, Star, Music, Heart, BookOpen } from 'lucide-react';

const VanderbiltUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Vanderbilt University";

    return (
        <div className="min-h-screen bg-[#fff] font-sans text-[#333] selection:bg-[#CFB53B] selection:text-[#000]">
            {/* Top Utility */}
            <div className="bg-[#1C1C1C] text-[#CFB53B] py-2 px-6 flex justify-end gap-6 text-[10px] font-bold uppercase tracking-wider">
                <a href="#" className="hover:text-white">A-Z</a>
                <a href="#" className="hover:text-white">People Finder</a>
                <a href="#" className="hover:text-white">Maps</a>
                <a href="#" className="hover:text-white">Anchor Link</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-5 px-6 md:px-12 sticky top-0 z-50 shadow-sm border-b border-[#CFB53B]">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Star className="text-[#CFB53B] fill-[#CFB53B]" size={28}/>
                        <div className="flex flex-col">
                             <span className="font-serif text-2xl font-bold uppercase tracking-widest text-[#1C1C1C] leading-none">{schoolName.split(' ')[0]}</span>
                             <span className="font-serif text-xs text-[#666] uppercase tracking-[0.3em] leading-none">University</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#1C1C1C] text-sm uppercase tracking-widest">
                        <a href="#" className="hover:text-[#CFB53B] transition-colors">About</a>
                        <a href="#" className="hover:text-[#CFB53B] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#CFB53B] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#CFB53B] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#CFB53B] transition-colors">Student Life</a>
                    </div>

                    <div className="flex gap-4">
                        <Search className="text-[#1C1C1C] cursor-pointer hover:text-[#CFB53B]" size={22} />
                        <Menu className="text-[#1C1C1C] cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Black & Gold */}
            <header className="relative h-[650px] bg-[#1C1C1C]">
                <img 
                    src="https://images.unsplash.com/photo-1590403756598-be98b7621c17?q=80&w=2070&auto=format&fit=crop" 
                    alt="Vanderbilt Campus" 
                    className="absolute inset-0 w-full h-full object-cover opacity-60 mix-blend-overlay"
                />
                
                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12 text-center">
                     <h1 className="text-5xl md:text-7xl font-serif text-[#CFB53B] mb-8 font-bold drop-shadow-xl">
                         Dare to Grow.
                     </h1>
                     <p className="text-xl md:text-2xl text-white max-w-2xl mx-auto mb-10 font-light leading-relaxed">
                         A community where you can grow intellectual, emotional, and social courage.
                     </p>
                     <Button className="bg-[#CFB53B] hover:bg-[#b0972b] text-[#1C1C1C] font-bold text-lg px-10 h-14 rounded-none uppercase tracking-widest">
                         Learn More
                     </Button>
                </div>
            </header>

            {/* Feature Blocks */}
            <section className="py-24 px-6 md:px-12 max-w-[1200px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                     <div className="text-center group cursor-pointer">
                         <div className="w-20 h-20 bg-[#f4f4f4] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#CFB53B] transition-colors">
                             <Heart size={32} className="text-[#1C1C1C]" />
                         </div>
                         <h3 className="text-xl font-bold uppercase tracking-widest mb-4 text-[#1C1C1C]">Immersion {schoolName.split(' ')[0]}</h3>
                         <p className="text-gray-600 leading-relaxed">Students engage in immersive experiences in and beyond the classroom.</p>
                     </div>
                     <div className="text-center group cursor-pointer">
                         <div className="w-20 h-20 bg-[#f4f4f4] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#CFB53B] transition-colors">
                             <Music size={32} className="text-[#1C1C1C]" />
                         </div>
                         <h3 className="text-xl font-bold uppercase tracking-widest mb-4 text-[#1C1C1C]">Music City</h3>
                         <p className="text-gray-600 leading-relaxed">Located in Nashville, a global hub for music, creativity and culture.</p>
                     </div>
                     <div className="text-center group cursor-pointer">
                         <div className="w-20 h-20 bg-[#f4f4f4] rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-[#CFB53B] transition-colors">
                             <BookOpen size={32} className="text-[#1C1C1C]" />
                         </div>
                         <h3 className="text-xl font-bold uppercase tracking-widest mb-4 text-[#1C1C1C]">Collaboration</h3>
                         <p className="text-gray-600 leading-relaxed">Breaking down silos to solve complex problems together.</p>
                     </div>
                </div>
            </section>

            {/* News / Feed */}
            <section className="bg-[#f9f9f9] py-24 px-6 md:px-12 border-t border-gray-200">
                <div className="max-w-[1200px] mx-auto">
                    <h2 className="text-3xl font-serif font-bold text-[#1C1C1C] mb-12 flex items-center gap-4">
                        Latest Stories <span className="h-px bg-[#CFB53B] flex-1"></span>
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="lg:col-span-2 relative h-[400px] group cursor-pointer overflow-hidden rounded-sm">
                            <img src="https://images.unsplash.com/photo-1497633762265-9d179a990aa6?q=80&w=2073&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-110" alt="Main"/>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-8">
                                <span className="text-[#CFB53B] uppercase font-bold text-xs tracking-widest mb-2">Research</span>
                                <h3 className="text-white text-2xl font-bold font-serif leading-tight">{schoolName.split(' ')[0]} engineers innovate new medical device</h3>
                            </div>
                        </div>
                        
                        <div className="bg-white p-6 shadow-sm border-t-4 border-[#CFB53B] hover:-translate-y-1 transition-transform cursor-pointer">
                            <span className="text-gray-400 text-xs font-bold uppercase mb-2 block">Academics</span>
                            <h4 className="text-lg font-bold text-[#1C1C1C] mb-4">Undergraduate researchers present at national conference</h4>
                            <p className="text-sm text-gray-500">Students showcase findings in biology, physics, and engineering.</p>
                        </div>
                        
                         <div className="bg-white p-6 shadow-sm border-t-4 border-[#1C1C1C] hover:-translate-y-1 transition-transform cursor-pointer">
                            <span className="text-gray-400 text-xs font-bold uppercase mb-2 block">Athletics</span>
                            <h4 className="text-lg font-bold text-[#1C1C1C] mb-4">Commodores win SEC championship in dramatic fashion</h4>
                            <p className="text-sm text-gray-500">A historic victory for the baseball program.</p>
                        </div>
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#1C1C1C] text-white py-16 px-6 md:px-12">
                <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row justify-between gap-12">
                    <div>
                         <span className="font-serif text-3xl font-bold uppercase tracking-widest text-[#CFB53B] mb-6 block">{schoolName.split(' ')[0]}</span>
                         <p className="text-gray-400 text-sm mb-2">Nashville, TN 37240</p>
                         <p className="text-gray-400 text-sm">(615) 322-7311</p>
                    </div>

                    <div className="flex gap-12 font-bold text-xs uppercase tracking-widest text-gray-400">
                        <ul className="space-y-4">
                            <li><a href="#" className="hover:text-[#CFB53B]">Careers</a></li>
                            <li><a href="#" className="hover:text-[#CFB53B]">Contact</a></li>
                             <li><a href="#" className="hover:text-[#CFB53B]">Sitemap</a></li>
                        </ul>
                         <ul className="space-y-4">
                            <li><a href="#" className="hover:text-[#CFB53B]">Privacy</a></li>
                            <li><a href="#" className="hover:text-[#CFB53B]">Accessibility</a></li>
                             <li><a href="#" className="hover:text-[#CFB53B]">TItle IX</a></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-[1200px] mx-auto mt-12 pt-8 border-t border-gray-800 text-center text-[10px] text-gray-600 uppercase tracking-widest">
                    © 2026 {schoolName} · All rights reserved
                </div>
            </footer>
        </div>
    );
};

export default VanderbiltUniversity;
